import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
const tiers = [{
  id: 'essential',
  name: 'Essential',
  price: '₹15L+',
  desc: 'Foundation',
  color: 'text-silver',
  bgColor: 'bg-silver',
  borderClass: 'border-silver',
  features: ['Standard Audio Zones', 'Smart Lighting Control', 'Basic Climate Integration', 'Single App Interface'],
  intensity: 1
}, {
  id: 'premium',
  name: 'Premium',
  price: '₹50L+',
  desc: 'Elevated',
  color: 'text-relax',
  bgColor: 'bg-relax',
  borderClass: 'border-relax',
  features: ['High-Fidelity Audio', 'Architectural Lighting', 'Motorized Shades', 'Advanced Scenes'],
  intensity: 2
}, {
  id: 'signature',
  name: 'Signature',
  price: '₹2Cr+',
  desc: 'Immersive',
  color: 'text-cinema',
  bgColor: 'bg-cinema',
  borderClass: 'border-cinema',
  features: ['Reference Grade Audio', 'Dedicated Cinema Room', 'Circadian Lighting', 'Invisible Tech Integration'],
  intensity: 3
}, {
  id: 'bespoke',
  name: 'Bespoke',
  price: '₹5Cr+',
  desc: 'Transcendent',
  color: 'text-music',
  bgColor: 'bg-music',
  borderClass: 'border-music',
  features: ['Concert-Grade Acoustics', 'AI-Driven Automation', 'Custom Interface Design', 'Uncompromised Luxury'],
  intensity: 4
}];
export function BudgetEngine() {
  const [sqft, setSqft] = useState(5000);
  const recommendedTier = sqft < 3000 ? 'essential' : sqft < 6000 ? 'premium' : sqft < 10000 ? 'signature' : 'bespoke';
  return <section className="py-32 bg-void relative border-t border-graphite" data-id="element-155">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-156">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-157">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-158">
            Investment Intelligence
          </h2>
          <p className="text-silver max-w-2xl mx-auto" data-id="element-159">
            Understanding what transforms at every level of investment.
          </p>
        </motion.div>

        {/* Size Slider */}
        <div className="max-w-2xl mx-auto mb-20 bg-carbon p-8 rounded-2xl border border-graphite" data-id="element-160">
          <div className="flex justify-between items-end mb-6" data-id="element-161">
            <span className="text-silver text-sm uppercase tracking-widest" data-id="element-162">
              Property Size
            </span>
            <span className="text-platinum font-serif text-2xl" data-id="element-163">
              {sqft.toLocaleString()} sq ft
            </span>
          </div>
          <input type="range" min="2000" max="15000" step="500" value={sqft} onChange={e => setSqft(Number(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer" style={{
          background: 'linear-gradient(to right, #8A8A8A, #10B981, #6366F1, #EC4899)'
        }} data-id="element-164" />
          <style dangerouslySetInnerHTML={{
          __html: `
            input[type=range]::-webkit-slider-thumb {
              -webkit-appearance: none;
              height: 20px;
              width: 20px;
              border-radius: 50%;
              background: #fff;
              box-shadow: 0 0 10px rgba(0,0,0,0.5);
              cursor: pointer;
            }
          `
        }} data-id="element-165" />
          <div className="flex justify-between mt-3 text-xs text-silver" data-id="element-166">
            <span data-id="element-167">2,000</span>
            <span data-id="element-168">15,000+</span>
          </div>
        </div>

        {/* Tiers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-169">
          {tiers.map((tier, idx) => {
          const isRecommended = tier.id === recommendedTier;
          return <motion.div key={tier.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: idx * 0.1
          }} className={`relative p-8 rounded-2xl transition-all duration-500 ${isRecommended ? `bg-obsidian border ${tier.borderClass} scale-105 z-10` : 'bg-carbon border border-graphite opacity-70 hover:opacity-100'}`} style={isRecommended ? {
            boxShadow: `0 0 30px var(--color-${tier.id === 'essential' ? 'silver' : tier.id === 'premium' ? 'relax' : tier.id === 'signature' ? 'cinema' : 'music'})`
          } : {}} data-id="element-170">
                {isRecommended && <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${tier.bgColor} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest`} data-id="element-171">
                    Recommended
                  </div>}

                <h3 className="text-silver text-sm uppercase tracking-widest mb-2" data-id="element-172">
                  {tier.name}
                </h3>
                <div className="text-title font-serif text-platinum mb-1" data-id="element-173">
                  {tier.desc}
                </div>
                <div className={`${tier.color} font-serif text-2xl mb-8`} data-id="element-174">
                  {tier.price}
                </div>

                {/* Intensity Indicator */}
                <div className="flex space-x-1 mb-8" data-id="element-175">
                  {[1, 2, 3, 4].map(i => <div key={i} className={`h-1 flex-1 rounded-full ${i <= tier.intensity ? tier.bgColor : 'bg-graphite'}`} data-id="element-176" />)}
                </div>

                <ul className="space-y-4" data-id="element-177">
                  {tier.features.map((feat, i) => <li key={i} className="flex items-start" data-id="element-178">
                      <CheckIcon className={`w-4 h-4 mr-3 mt-0.5 shrink-0 ${isRecommended ? tier.color : 'text-silver'}`} data-id="element-179" />
                      <span className="text-sm text-platinum" data-id="element-180">{feat}</span>
                    </li>)}
                </ul>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}